import React from 'react';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core';

import { colors } from 'src/themes/colors';

import MHInput from '../MHInput';
import { InvoiceRating } from './InvoiceRating';

interface Props {
  organizationImprovements: string;
  setOrganizationImprovements: (code: string) => void;
  abilityToHelpClient: number;
  setAbilityToHelpClient: (code: number) => void;
  workedOnAgreedUponGoals: number;
  setWorkedOnAgreedUponGoals: (codes: number) => void;
  clearPlanToWorkTowardGoals: number;
  setClearPlanToWorkTowardGoals: (codes: number) => void;
  showBilling: boolean;
  disabled: boolean;
}

export enum RateSessionFormTestID {
  Container = 'RateSessionFormTestID.Container',
}

const useStyles = makeStyles({
  wrapper: {
    position: 'relative',
  },
  disabledCover: {
    backgroundColor: 'white',
    opacity: 0.7,
    zIndex: 25,
    height: '100%',
    width: '100%',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    position: 'absolute',
    top: '0px',
    left: '0px',
  },
  visitRow: {
    margin: '0px 36px 15px',
  },
  visitRowRight: {
    display: 'inline-block',
    margin: '0px 0px 15px 36px',
  },
  dialogContentRightInner: {
    display: 'inline-flex',
    flexDirection: 'column',
    width: '100%',
    paddingRight: 36,
  },
  formLabel: {
    fontFamily: 'Avenir-Heavy',
    fontSize: 14,
    color: colors.black3,
  },
  textArea: {
    color: colors.gray18,
    '& > div': {
      border: 'none',
    },
    '& fieldset': {
      borderColor: colors.gray15,
      borderWidth: 1,
      borderRadius: 6,
    },
    '&:hover fieldset': {
      borderColor: `${colors.blue6} !important`,
    },
    '&:focus fieldset': {
      borderColor: `${colors.blue6} !important`,
      borderWidth: 2,
    },
  },
  textAreaWrapper: {
    flex: '1',
    margin: '0px 0px 15px 36px',
  },
});

export const RateSessionForm: React.FC<Props> = ({
  organizationImprovements,
  setOrganizationImprovements,
  abilityToHelpClient,
  setAbilityToHelpClient,
  workedOnAgreedUponGoals,
  setWorkedOnAgreedUponGoals,
  clearPlanToWorkTowardGoals,
  setClearPlanToWorkTowardGoals,
  showBilling,
  disabled,
}: Props) => {
  const classes = useStyles();
  const disabledCover = disabled ? classes.disabledCover : '';

  return (
    <div className={classes.wrapper}>
      <div className={disabledCover}></div>
      <div className={classes.visitRow}>
        <span className="font-extrabold text-gray-900 text-xl">
          <span className="text-blue-400">Step {showBilling ? '3' : '2'}:</span> Rate your session
        </span>
      </div>

      <div className={classes.dialogContentRightInner}>
        <div className={classes.visitRowRight}>
          <InvoiceRating
            header={
              <Typography className={classes.formLabel}>
                I am confident in my ability to help this client
              </Typography>
            }
            ratingValue={abilityToHelpClient}
            onClick={setAbilityToHelpClient}
          />
        </div>

        <div className={classes.visitRowRight}>
          <InvoiceRating
            header={
              <Typography className={classes.formLabel}>
                This client and I are working on mutually agreed upon goals
              </Typography>
            }
            ratingValue={workedOnAgreedUponGoals}
            onClick={setWorkedOnAgreedUponGoals}
          />
        </div>

        <div className={classes.visitRowRight}>
          <InvoiceRating
            header={
              <Typography className={classes.formLabel}>
                The client left the session with an action item or skill to practice
              </Typography>
            }
            ratingValue={clearPlanToWorkTowardGoals}
            onClick={setClearPlanToWorkTowardGoals}
          />
        </div>

        <div className={`${classes.textAreaWrapper} mb-0`}>
          <Typography className={classes.formLabel}>
            Share any additional feedback about this session
          </Typography>
          <MHInput
            value={organizationImprovements}
            onChange={(e) => e.target && setOrganizationImprovements(e.target.value)}
            multiline
            rows={5}
            inputClasses={classes.textArea}
            placeholder="Let us know how we can create a better experience for you and your clients"
          />
        </div>
      </div>
    </div>
  );
};
